import { calculatePixelLimit } from '@/domain/export-import/pixel-limit';
import type { ImageScale } from '@/domain/export-import/contracts';
import { waitForExportAssets } from './wait-for-assets';

export class VisualExportError extends Error {}

async function renderPngBlob(
  surface: HTMLElement,
  options: { scale: ImageScale; transparent: boolean },
): Promise<{ blob: Blob; warnings: string[] }> {
  const bounds = surface.getBoundingClientRect();
  const limit = calculatePixelLimit(bounds.width, bounds.height, options.scale);
  if (!limit.safe) {
    throw new VisualExportError(
      `This export is too large at ${options.scale}x.${limit.suggestedScale ? ` Try ${limit.suggestedScale}x.` : ''}`,
    );
  }
  const warnings = await waitForExportAssets(surface);
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
  const { default: html2canvas } = await import('html2canvas');
  const canvas = await html2canvas(surface, {
    width: bounds.width,
    height: bounds.height,
    scale: options.scale,
    backgroundColor: options.transparent ? null : '#090b0a',
    useCORS: true,
    logging: false,
    ignoreElements: (node) => node instanceof HTMLElement && node.dataset.exportExclude === 'true',
  });
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new VisualExportError('The browser could not generate the PNG image.');
  return { blob, warnings };
}

export async function exportPng(
  surface: HTMLElement,
  options: { scale: ImageScale; transparent: boolean },
) {
  return renderPngBlob(surface, options);
}

export async function exportPdf(surface: HTMLElement): Promise<{ blob: Blob; warnings: string[] }> {
  const { jsPDF } = await import('jspdf');
  const pages = [...surface.querySelectorAll<HTMLElement>('[data-export-page]')];
  const exportPages = pages.length ? pages : [surface];
  const warnings = new Set<string>();
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;

  for (const [index, page] of exportPages.entries()) {
    const rendered = await renderPngBlob(page, { scale: 2, transparent: false });
    rendered.warnings.forEach((warning) => warnings.add(warning));
    const dataUrl = await blobToDataUrl(rendered.blob);
    if (index > 0) pdf.addPage('a4', 'landscape');
    const ratio = page.offsetWidth / page.offsetHeight;
    let width = pageWidth - margin * 2;
    let height = width / ratio;
    if (height > pageHeight - margin * 2) {
      height = pageHeight - margin * 2;
      width = height * ratio;
    }
    pdf.addImage(dataUrl, 'PNG', (pageWidth - width) / 2, (pageHeight - height) / 2, width, height);
  }
  return { blob: pdf.output('blob'), warnings: [...warnings] };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new VisualExportError('The rendered image could not be read.'));
    reader.readAsDataURL(blob);
  });
}
